/**
 * Admin Dashboard — Comprehensive founder/admin analytics.
 * Owner-gated. Single source of truth for all business metrics.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  RefreshControl,
  Switch,
  useWindowDimensions,
  Share,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  ChevronLeft, ChevronDown, ChevronUp, Download,
  DollarSign, TrendingUp, Activity, BarChart3,
  Users, Target, Swords, Heart, Shield, Trophy,
  Zap, Clock, Filter,
} from 'lucide-react-native';
import { FONT, WEIGHT, SPACE, RADIUS } from '@/constants/tokens';
import { TYPO } from '@/constants/typography';
import {
  type UserRow, type RevenueMetrics, type GrowthMetrics,
  type EngagementMetrics, type RetentionMetrics, type CohortRow,
  type FunnelStep, type ContentMetrics, type AppHealthMetrics,
  TIER_PRICES, filterTestUsers, isTestAccount,
  formatNum, formatMoney, pct,
} from '@/lib/adminQueries';

// ─── Access Control ──────────────────────────────────────────────
// Exact-match only — no domain-wide access
const ADMIN_EMAIL = 'nathanael@douevenlift.com';

function isAuthorized(profile: any): boolean {
  if (__DEV__) return true;
  if (profile?.is_admin === true) return true;
  if (profile?.email === ADMIN_EMAIL) return true;
  return false;
}

// ─── Gold accent color ──────────────────────────────────────────
const GOLD = '#D4A843';
const GOLD_DIM = 'rgba(212,168,67,0.15)';

// ─── Main Component ─────────────────────────────────────────────
export default function AdminDashboard() {
  const router = useRouter();
  const { theme } = useTheme();
  const { profile } = useAuth();
  const c = theme.colors;
  const { width } = useWindowDimensions();
  const cols = width > 900 ? 4 : width > 600 ? 3 : 2;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [excludeTest, setExcludeTest] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Section collapse state
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    revenue: true, growth: true,
    engagement: false, retention: false, funnel: false,
    content: false, health: false,
  });

  // Data
  const [revenue, setRevenue] = useState<RevenueMetrics | null>(null);
  const [growth, setGrowth] = useState<GrowthMetrics | null>(null);
  const [engagement, setEngagement] = useState<EngagementMetrics | null>(null);
  const [retention, setRetention] = useState<RetentionMetrics | null>(null);
  const [funnel, setFunnel] = useState<FunnelStep[]>([]);
  const [content, setContent] = useState<ContentMetrics | null>(null);
  const [health, setHealth] = useState<AppHealthMetrics | null>(null);
  const [allUsers, setAllUsers] = useState<UserRow[]>([]);

  const loadedRef = useRef(false);

  // Load test filter preference
  useEffect(() => {
    AsyncStorage.getItem('admin:excludeTestAccounts').then(v => {
      if (v === 'true') setExcludeTest(true);
    });
  }, []);

  const toggleExcludeTest = (val: boolean) => {
    setExcludeTest(val);
    AsyncStorage.setItem('admin:excludeTestAccounts', String(val));
  };

  // ─── Data Loading ───────────────────────────────────────────
  const loadData = useCallback(async () => {
    setError(null);
    try {
      const now = new Date();
      const dayAgo = new Date(now.getTime() - 1 * 86400000);
      const weekAgo = new Date(now.getTime() - 7 * 86400000);
      const monthAgo = new Date(now.getTime() - 30 * 86400000);

      // 1. Fetch all profiles
      const profileSelect = 'id, handle, display_name, gender, bodyweight, units, lifting_score, duel_standard_strength_kg, created_at, date_of_birth, subscription_tier, home_gym_name, onboarding_completed';
      const { data: rawProfiles, error: pErr } = await supabase
        .from('profiles')
        .select(profileSelect)
        .order('created_at', { ascending: false });
      if (pErr) throw pErr;

      const users = filterTestUsers((rawProfiles || []) as unknown as UserRow[], excludeTest);
      setAllUsers(users);
      const userIdSet = new Set(users.map(u => u.id));

      // 2. Fetch all workouts
      const { data: rawWorkouts } = await supabase
        .from('workouts')
        .select('id, user_id, performed_at, payload, created_at');
      const workouts = (rawWorkouts || []).filter((w: any) => userIdSet.has(w.user_id));

      // 3. Fetch all duels
      const { data: rawDuels } = await supabase
        .from('duels')
        .select('id, status, duel_type, timeframe_hours, created_at, challenger_id, challenged_id');
      const duels = (rawDuels || []).filter((d: any) =>
        userIdSet.has(d.challenger_id) || userIdSet.has(d.challenged_id)
      );

      // 4. Fetch follows
      const { count: followCount } = await supabase
        .from('follows')
        .select('id', { count: 'exact', head: true });

      // ── Workout maps ──
      const workoutsByUser = new Map<string, any[]>();
      workouts.forEach((w: any) => {
        const list = workoutsByUser.get(w.user_id) || [];
        list.push(w);
        workoutsByUser.set(w.user_id, list);
      });

      // ── REVENUE ──
      const tierCounts: Record<string, number> = { free: 0, pro: 0, coach_pro: 0, coach: 0, org_pro: 0, club_starter: 0, club_growth: 0, club_performance: 0, club_elite: 0 };
      users.forEach(u => {
        const t = (u.subscription_tier || 'free').toLowerCase();
        tierCounts[t] = (tierCounts[t] || 0) + 1;
      });
      const payingUsers = users.filter(u => {
        const t = (u.subscription_tier || 'free').toLowerCase();
        return t !== 'free' && (TIER_PRICES[t] || 0) > 0;
      }).length;
      const mrr = Object.entries(tierCounts).reduce((sum, [t, c]) => sum + (TIER_PRICES[t] || 0) * c, 0);
      const arpu = payingUsers > 0 ? mrr / payingUsers : 0;
      // Churn: users who joined > 30 days ago and are free (simple proxy)
      const oldFreeUsers = users.filter(u =>
        new Date(u.created_at) < monthAgo &&
        (u.subscription_tier || 'free').toLowerCase() === 'free'
      ).length;
      const oldUsers = users.filter(u => new Date(u.created_at) < monthAgo).length;
      const churnRate = oldUsers > 0 ? pct(oldFreeUsers, oldUsers) : 0;
      const avgLifespanMonths = 6; // placeholder
      setRevenue({
        mrr, arr: mrr * 12, arpu, tierCounts, payingUsers,
        churnCount: oldFreeUsers, churnRate,
        ltv: arpu * avgLifespanMonths,
      });

      // ── GROWTH ──
      const dauIds = new Set(workouts.filter((w: any) => new Date(w.performed_at || w.created_at) >= dayAgo).map((w: any) => w.user_id));
      const wauIds = new Set(workouts.filter((w: any) => new Date(w.performed_at || w.created_at) >= weekAgo).map((w: any) => w.user_id));
      const mauIds = new Set(workouts.filter((w: any) => new Date(w.performed_at || w.created_at) >= monthAgo).map((w: any) => w.user_id));

      const newThisWeek = users.filter(u => new Date(u.created_at) >= weekAgo).length;
      const newThisMonth = users.filter(u => new Date(u.created_at) >= monthAgo).length;

      // Weekly growth chart (12 weeks)
      const weeklyGrowth: { label: string; count: number }[] = [];
      for (let i = 11; i >= 0; i--) {
        const ws = new Date(now.getTime() - (i + 1) * 7 * 86400000);
        const we = new Date(now.getTime() - i * 7 * 86400000);
        const cnt = users.filter(u => { const d = new Date(u.created_at); return d >= ws && d < we; }).length;
        weeklyGrowth.push({ label: ws.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), count: cnt });
      }

      // Gym stats
      const gymMembers = new Map<string, Set<string>>();
      users.forEach(u => {
        if (u.home_gym_name) {
          const s = gymMembers.get(u.home_gym_name) || new Set();
          s.add(u.id);
          gymMembers.set(u.home_gym_name, s);
        }
      });
      // Count actual gyms in the database (not just user-referenced ones)
      const { count: actualGymCount } = await supabase.from('gyms').select('id', { count: 'exact', head: true });
      const totalGyms = actualGymCount ?? gymMembers.size;
      // Count gyms with at least one active member
      const { count: activeGymCount } = await supabase
        .from('gym_memberships')
        .select('gym_id', { count: 'exact', head: true })
        .eq('status', 'active');
      const activeGyms = activeGymCount ?? 0;
      // Waitlist count
      const { count: waitlistCount } = await supabase
        .from('waitlist')
        .select('id', { count: 'exact', head: true });
      // Pending gym claims
      const { count: pendingClaims } = await supabase
        .from('gym_memberships')
        .select('id', { count: 'exact', head: true })
        .eq('role', 'owner')
        .eq('status', 'pending');
      const newGymsThisMonth = (() => {
        const gymsFromOldUsers = new Set<string>();
        const gymsFromNewUsers = new Set<string>();
        users.forEach(u => {
          if (!u.home_gym_name) return;
          if (new Date(u.created_at) < monthAgo) gymsFromOldUsers.add(u.home_gym_name);
          else gymsFromNewUsers.add(u.home_gym_name);
        });
        let count = 0;
        gymsFromNewUsers.forEach(g => { if (!gymsFromOldUsers.has(g)) count++; });
        return count;
      })();
      const avgMembersPerGym = totalGyms > 0 ? Math.round((users.filter(u => u.home_gym_name).length / totalGyms) * 10) / 10 : 0;

      setGrowth({
        dau: dauIds.size, wau: wauIds.size, mau: mauIds.size,
        newThisWeek, newThisMonth, weeklyGrowth,
        totalGyms, activeGyms, newGymsThisMonth, avgMembersPerGym,
        waitlistCount: waitlistCount ?? 0,
        pendingClaims: pendingClaims ?? 0,
      });

      // ── ENGAGEMENT ──
      const usersWithWorkouts = new Set(workouts.map((w: any) => w.user_id));
      const usersInDuels = new Set(duels.flatMap((d: any) => [d.challenger_id, d.challenged_id].filter(Boolean)));

      // Avg workouts per user per week (last 4 weeks)
      const fourWeeksAgo = new Date(now.getTime() - 28 * 86400000);
      const recentWorkouts = workouts.filter((w: any) => new Date(w.performed_at || w.created_at) >= fourWeeksAgo);
      const recentWorkoutUsers = new Set(recentWorkouts.map((w: any) => w.user_id));
      const avgWpupw = recentWorkoutUsers.size > 0 ? Math.round((recentWorkouts.length / recentWorkoutUsers.size / 4) * 10) / 10 : 0;

      // Avg duels per user per month
      const recentDuels = duels.filter((d: any) => new Date(d.created_at) >= monthAgo);
      const recentDuelUsers = new Set(recentDuels.flatMap((d: any) => [d.challenger_id, d.challenged_id].filter(Boolean)));
      const avgDpupm = recentDuelUsers.size > 0 ? Math.round((recentDuels.length / recentDuelUsers.size) * 10) / 10 : 0;

      // Feature adoption
      const pctLoggedWorkout = pct(usersWithWorkouts.size, users.length);
      const pctCreatedDuel = pct(usersInDuels.size, users.length);
      // Core 5: check for 5+ distinct exercises in workouts (proxy)
      let core5Users = 0;
      workoutsByUser.forEach((wList) => {
        const exercises = new Set<string>();
        wList.forEach((w: any) => {
          const payload = typeof w.payload === 'string' ? JSON.parse(w.payload) : w.payload;
          (payload?.exercises ?? []).forEach((ex: any) => { if (ex.name) exercises.add(ex.name); });
        });
        if (exercises.size >= 5) core5Users++;
      });
      const pctCompletedCore5 = pct(core5Users, users.length);

      // Follow adoption
      const followersSet = new Set<string>();
      // Use followCount as proxy
      const pctFollowedSomeone = followCount ? pct(Math.min(followCount, users.length), users.length) : 0;

      // Peak hour
      const hourCounts = new Array(24).fill(0);
      workouts.forEach((w: any) => {
        const d = new Date(w.performed_at || w.created_at);
        if (!isNaN(d.getTime())) hourCounts[d.getHours()]++;
      });
      const peakH = hourCounts.indexOf(Math.max(...hourCounts));
      const peakHEnd = (peakH + 1) % 24;
      const fmtH = (h: number) => h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;

      setEngagement({
        avgWorkoutsPerUserPerWeek: avgWpupw,
        avgDuelsPerUserPerMonth: avgDpupm,
        avgWorkoutDuration: 'N/A', // not tracked
        pctLoggedWorkout, pctCreatedDuel, pctCompletedCore5, pctFollowedSomeone,
        peakHour: `${fmtH(peakH)}–${fmtH(peakHEnd)}`,
      });

      // ── RETENTION ──
      const retCalc = (days: number) => {
        let total = 0, retained = 0;
        users.forEach(u => {
          const signup = new Date(u.created_at).getTime();
          if ((now.getTime() - signup) / 86400000 < days) return;
          total++;
          const uWorkouts = workoutsByUser.get(u.id) || [];
          if (uWorkouts.some((w: any) => {
            const t = new Date(w.performed_at || w.created_at).getTime();
            return t >= signup + days * 86400000 * 0.8;
          })) retained++;
        });
        return total > 0 ? Math.round((retained / total) * 100) : 0;
      };

      // Cohorts: last 4 weeks
      const cohorts: CohortRow[] = [];
      for (let i = 0; i < 4; i++) {
        const cs = new Date(now.getTime() - (i + 1) * 7 * 86400000);
        const ce = new Date(now.getTime() - i * 7 * 86400000);
        const cohortUsers = users.filter(u => {
          const d = new Date(u.created_at);
          return d >= cs && d < ce;
        });
        const size = cohortUsers.length;
        const cohortRetention = (days: number) => {
          if (size === 0) return 0;
          let ret = 0;
          cohortUsers.forEach(u => {
            const signup = new Date(u.created_at).getTime();
            if ((now.getTime() - signup) / 86400000 < days) return;
            const uW = workoutsByUser.get(u.id) || [];
            if (uW.some((w: any) => new Date(w.performed_at || w.created_at).getTime() >= signup + days * 86400000 * 0.8)) ret++;
          });
          return Math.round((ret / size) * 100);
        };
        const ageDays = Math.floor((now.getTime() - cs.getTime()) / 86400000);
        cohorts.push({
          label: cs.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          size, d1: cohortRetention(1), d7: cohortRetention(7), d30: cohortRetention(30),
          ageDays,
        });
      }

      // Churn by tier (simple: users inactive 30d+ by tier)
      const churnByTier: Record<string, number> = {};
      users.forEach(u => {
        const tier = (u.subscription_tier || 'free').toLowerCase();
        const uW = workoutsByUser.get(u.id) || [];
        const lastActivity = uW.reduce((max: number, w: any) => {
          const t = new Date(w.performed_at || w.created_at).getTime();
          return t > max ? t : max;
        }, 0);
        if (lastActivity > 0 && (now.getTime() - lastActivity) > 30 * 86400000) {
          churnByTier[tier] = (churnByTier[tier] || 0) + 1;
        }
      });

      setRetention({
        d1: retCalc(1), d7: retCalc(7), d30: retCalc(30), d90: retCalc(90),
        cohorts, churnByTier,
      });

      // ── FUNNEL ──
      const totalSignups = users.length;
      const onboarded = users.filter(u => u.onboarding_completed).length;
      const firstWorkout = usersWithWorkouts.size;
      const firstDuel = users.filter(u => usersInDuels.has(u.id)).length;
      const subscribed = users.filter(u => (u.subscription_tier || 'free').toLowerCase() !== 'free').length;

      const funnelSteps: FunnelStep[] = [
        { label: 'Signup', count: totalSignups, pct: 100, dropOff: 0 },
        { label: 'Onboarded', count: onboarded, pct: pct(onboarded, totalSignups), dropOff: pct(totalSignups - onboarded, totalSignups) },
        { label: 'First Workout', count: firstWorkout, pct: pct(firstWorkout, totalSignups), dropOff: pct(onboarded - firstWorkout, totalSignups) },
        { label: 'Core 5 Done', count: core5Users, pct: pct(core5Users, totalSignups), dropOff: pct(firstWorkout - core5Users, totalSignups) },
        { label: 'First Duel', count: firstDuel, pct: pct(firstDuel, totalSignups), dropOff: pct(core5Users - firstDuel, totalSignups) },
        { label: 'Subscribed', count: subscribed, pct: pct(subscribed, totalSignups), dropOff: pct(firstDuel - subscribed, totalSignups) },
      ];
      setFunnel(funnelSteps);

      // ── CONTENT ──
      const exerciseMap = new Map<string, number>();
      const gymWorkoutMap = new Map<string, number>();
      let totalSets = 0;
      let totalVol = 0;

      workouts.forEach((w: any) => {
        const payload = typeof w.payload === 'string' ? JSON.parse(w.payload) : w.payload;
        (payload?.exercises ?? []).forEach((ex: any) => {
          const name = ex.name || 'Unknown';
          exerciseMap.set(name, (exerciseMap.get(name) || 0) + 1);
          (ex.sets ?? []).forEach((s: any) => {
            totalSets++;
            totalVol += (Number(s.weight) || 0) * (Number(s.reps) || 0);
          });
        });
        // gym workout count
        const uid = w.user_id;
        const user = users.find(u => u.id === uid);
        if (user?.home_gym_name) {
          gymWorkoutMap.set(user.home_gym_name, (gymWorkoutMap.get(user.home_gym_name) || 0) + 1);
        }
      });

      const topExercises = Array.from(exerciseMap.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([name, count]) => ({ name, count }));

      const mostActiveGyms = Array.from(gymWorkoutMap.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([name, workouts]) => ({ name, workouts }));

      setContent({
        topExercises, mostActiveGyms,
        avgSetsPerWorkout: workouts.length > 0 ? Math.round((totalSets / workouts.length) * 10) / 10 : 0,
        totalVolume: totalVol,
      });

      // ── APP HEALTH ──
      const { count: flagsTotal } = await supabase.from('integrity_flags').select('id', { count: 'exact', head: true });
      const { count: flagsUnresolved } = await supabase.from('integrity_flags').select('id', { count: 'exact', head: true }).eq('resolved', false);
      const { count: flagsResolvedWeek } = await supabase.from('integrity_flags').select('id', { count: 'exact', head: true }).eq('resolved', true).gte('updated_at', weekAgo.toISOString());

      const { count: seasonCount } = await supabase.from('seasons').select('id', { count: 'exact', head: true }).eq('active', true);
      const { count: seasonParts } = await supabase.from('user_challenges').select('id', { count: 'exact', head: true });
      const { count: challengeSubs } = await supabase.from('user_challenges').select('id', { count: 'exact', head: true }).gte('created_at', weekAgo.toISOString());

      setHealth({
        flagsTotal: flagsTotal ?? 0,
        flagsUnresolved: flagsUnresolved ?? 0,
        flagsResolvedThisWeek: flagsResolvedWeek ?? 0,
        activeSeasons: seasonCount ?? 0,
        seasonParticipants: seasonParts ?? 0,
        challengeSubmissionRate: (seasonParts ?? 0) > 0 ? pct(challengeSubs ?? 0, seasonParts ?? 0) : 0,
      });

    } catch (err: any) {
      console.error('[AdminDash]', err);
      setError(err?.message || String(err));
    }
  }, [excludeTest]);

  useEffect(() => {
    if (isAuthorized(profile)) {
      setLoading(true);
      loadData().finally(() => { setLoading(false); loadedRef.current = true; });
    }
  }, [profile, loadData]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  }, [loadData]);

  // ─── Export ──────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const headers = ['ID', 'Handle', 'Display Name', 'Gender', 'Subscription Tier', 'Home Gym', 'Lifting Score', 'Created At'];
      const rows = allUsers.map(u => [
        u.id, u.handle || '', u.display_name || '',
        u.gender || '', u.subscription_tier || 'free', u.home_gym_name || '',
        u.lifting_score ?? '', u.created_at,
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
      const csv = [headers.join(','), ...rows].join('\n');

      if (Platform.OS === 'web') {
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `duel-admin-export-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        await Share.share({ message: csv, title: 'DUEL Admin Export' });
      }
    } catch (err: any) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  // ─── Access check ────────────────────────────────────────────
  if (!isAuthorized(profile)) {
    return (
      <SafeAreaView style={[s.root, { backgroundColor: c.background }]} edges={['top']}>
        <View style={s.center}>
          <Shield size={48} color={c.textTertiary} />
          <Text style={[s.accessDenied, { color: c.textSecondary }]}>Access Denied</Text>
          <Text style={[s.accessSub, { color: c.textTertiary }]}>Owner accounts only.</Text>
        </View>
      </SafeAreaView>
    );
  }

  // ─── UI Helpers ──────────────────────────────────────────────
  const toggle = (key: string) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const Section = ({ id, icon, title, children }: { id: string; icon: React.ReactNode; title: string; children: React.ReactNode }) => (
    <View style={[s.section, { borderColor: c.border }]}>
      <TouchableOpacity style={s.sectionHeader} onPress={() => toggle(id)} activeOpacity={0.7}>
        {icon}
        <Text style={[s.sectionTitle, { color: c.text }]}>{title}</Text>
        {expanded[id] ? <ChevronUp size={18} color={c.textTertiary} /> : <ChevronDown size={18} color={c.textTertiary} />}
      </TouchableOpacity>
      {expanded[id] && <View style={s.sectionBody}>{children}</View>}
    </View>
  );

  const Stat = ({ value, label, gold, delta }: { value: string | number; label: string; gold?: boolean; delta?: string }) => (
    <View style={[s.stat, { backgroundColor: c.surface, borderColor: c.border, width: `${100 / cols - 2}%` as any }]}>
      <Text style={[s.statVal, { color: gold ? GOLD : c.text }]}>{value}</Text>
      <Text style={[s.statLabel, { color: c.textTertiary }]}>{label}</Text>
      {delta && <Text style={[s.delta, { color: delta.startsWith('↑') ? '#4CAF50' : '#F44336' }]}>{delta}</Text>}
    </View>
  );

  const Bar = ({ label, value, max, suffix }: { label: string; value: number; max: number; suffix?: string }) => {
    const w = max > 0 ? Math.max((value / max) * 100, 2) : 2;
    return (
      <View style={s.barRow}>
        <Text style={[s.barLabel, { color: c.textSecondary }]} numberOfLines={1}>{label}</Text>
        <View style={[s.barTrack, { backgroundColor: 'rgba(128,128,128,0.12)' }]}>
          <View style={[s.barFill, { backgroundColor: GOLD, width: `${w}%` as any }]} />
        </View>
        <Text style={[s.barVal, { color: c.text }]}>{value}{suffix || ''}</Text>
      </View>
    );
  };

  const RankedRow = ({ rank, name, value, sub }: { rank: number; name: string; value: string; sub?: string }) => (
    <View style={[s.rankedRow, { borderBottomColor: c.border }]}>
      <Text style={[s.rank, { color: c.textTertiary }]}>{rank}</Text>
      <View style={{ flex: 1 }}>
        <Text style={[s.rankedName, { color: c.text }]}>{name}</Text>
        {sub && <Text style={[s.rankedSub, { color: c.textTertiary }]}>{sub}</Text>}
      </View>
      <Text style={[s.rankedVal, { color: GOLD }]}>{value}</Text>
    </View>
  );

  return (
    <SafeAreaView style={[s.root, { backgroundColor: c.background }]} edges={['top']}>
      {/* Header */}
      <View style={[s.header, { borderBottomColor: c.border }]}>
        <TouchableOpacity onPress={() => router.back()} hitSlop={12}>
          <ChevronLeft size={22} color={c.textSecondary} />
        </TouchableOpacity>
        <Text style={[s.headerTitle, { color: GOLD }]}>ADMIN DASHBOARD</Text>
        <TouchableOpacity onPress={handleExport} disabled={exporting} hitSlop={12}>
          {exporting ? <ActivityIndicator size="small" color={GOLD} /> : <Download size={20} color={GOLD} />}
        </TouchableOpacity>
      </View>

      {/* Test filter toggle */}
      <View style={[s.filterRow, { borderBottomColor: c.border, backgroundColor: c.surface }]}>
        <Filter size={14} color={c.textTertiary} />
        <Text style={[s.filterLabel, { color: c.textSecondary }]}>Exclude test accounts</Text>
        <Switch value={excludeTest} onValueChange={toggleExcludeTest} trackColor={{ true: GOLD_DIM, false: 'rgba(128,128,128,0.2)' }} thumbColor={excludeTest ? GOLD : '#888'} />
      </View>

      {loading ? (
        <View style={s.center}><ActivityIndicator size="large" color={GOLD} /></View>
      ) : (
        <ScrollView
          contentContainerStyle={s.scroll}
          showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={GOLD} />}
        >
          {error && (
            <View style={[s.errorBox, { backgroundColor: 'rgba(244,67,54,0.1)' }]}>
              <Text style={{ color: '#F44336', fontSize: FONT.sm, fontWeight: WEIGHT.bold }}>Error</Text>
              <Text style={{ color: '#EF9A9A', fontSize: FONT.xs }}>{error}</Text>
            </View>
          )}

          {/* ═══ REVENUE ═══ */}
          <Section id="revenue" icon={<DollarSign size={18} color={GOLD} />} title="Revenue & Monetization">
            <View style={s.statGrid}>
              <Stat value={formatMoney(revenue?.mrr ?? 0)} label="MRR" gold />
              <Stat value={formatMoney(revenue?.arr ?? 0)} label="ARR" gold />
              <Stat value={formatMoney(revenue?.arpu ?? 0)} label="ARPU" />
              <Stat value={formatMoney(revenue?.ltv ?? 0)} label="Est. LTV" />
              <Stat value={revenue?.payingUsers ?? 0} label="Paying Users" />
              <Stat value={`${revenue?.churnRate ?? 0}%`} label="Churn Rate" />
            </View>
            <Text style={[s.subHead, { color: c.textTertiary }]}>SUBSCRIBERS BY TIER</Text>
            {revenue && Object.entries(revenue.tierCounts).sort(([, a], [, b]) => b - a).map(([tier, cnt]) => (
              <Bar key={tier} label={`${tier} (${formatMoney(TIER_PRICES[tier] || 0)}/mo)`} value={cnt} max={allUsers.length} />
            ))}
          </Section>

          {/* ═══ GROWTH ═══ */}
          <Section id="growth" icon={<TrendingUp size={18} color={GOLD} />} title="Growth">
            <View style={s.statGrid}>
              <Stat value={growth?.dau ?? 0} label="DAU" gold />
              <Stat value={growth?.wau ?? 0} label="WAU" gold />
              <Stat value={growth?.mau ?? 0} label="MAU" gold />
              <Stat value={growth?.newThisWeek ?? 0} label="New (7d)" />
              <Stat value={growth?.newThisMonth ?? 0} label="New (30d)" />
              <Stat value="N/A" label="Viral Coeff." />
            </View>
            <Text style={[s.subHead, { color: c.textTertiary }]}>GYM NETWORK</Text>
            <View style={s.statGrid}>
              <Stat value={growth?.totalGyms ?? 0} label="Total Gyms" />
              <Stat value={growth?.activeGyms ?? 0} label="Active Gyms" gold />
              <Stat value={growth?.newGymsThisMonth ?? 0} label="New Gyms (30d)" />
              <Stat value={growth?.avgMembersPerGym ?? 0} label="Avg Members/Gym" />
              <Stat value={growth?.waitlistCount ?? 0} label="Waitlist" />
              <Stat value={growth?.pendingClaims ?? 0} label="Gym Claims" />
            </View>
            <Text style={[s.subHead, { color: c.textTertiary }]}>SIGNUPS / WEEK (12 WK)</Text>
            {growth?.weeklyGrowth.map((w, i) => (
              <Bar key={i} label={w.label} value={w.count} max={Math.max(...(growth?.weeklyGrowth.map(x => x.count) || [1]))} />
            ))}
          </Section>

          {/* ═══ ENGAGEMENT ═══ */}
          <Section id="engagement" icon={<Activity size={18} color={GOLD} />} title="Engagement">
            <View style={s.statGrid}>
              <Stat value={engagement?.avgWorkoutsPerUserPerWeek ?? 0} label="Workouts/User/Wk" gold />
              <Stat value={engagement?.avgDuelsPerUserPerMonth ?? 0} label="Duels/User/Mo" />
              <Stat value={engagement?.avgWorkoutDuration ?? 'N/A'} label="Avg Duration" />
            </View>
            <Text style={[s.subHead, { color: c.textTertiary }]}>FEATURE ADOPTION</Text>
            <Bar label="Logged a Workout" value={engagement?.pctLoggedWorkout ?? 0} max={100} suffix="%" />
            <Bar label="Created a Duel" value={engagement?.pctCreatedDuel ?? 0} max={100} suffix="%" />
            <Bar label="Core 5 Complete" value={engagement?.pctCompletedCore5 ?? 0} max={100} suffix="%" />
            <Bar label="Followed Someone" value={engagement?.pctFollowedSomeone ?? 0} max={100} suffix="%" />
            <View style={[s.peakRow, { backgroundColor: GOLD_DIM, borderColor: GOLD }]}>
              <Clock size={14} color={GOLD} />
              <Text style={[s.peakText, { color: GOLD }]}>Peak: {engagement?.peakHour ?? '—'}</Text>
            </View>
          </Section>

          {/* ═══ RETENTION ═══ */}
          <Section id="retention" icon={<Target size={18} color={GOLD} />} title="Retention">
            <View style={s.statGrid}>
              <Stat value={`${retention?.d1 ?? 0}%`} label="D1" gold />
              <Stat value={`${retention?.d7 ?? 0}%`} label="D7" gold />
              <Stat value={`${retention?.d30 ?? 0}%`} label="D30" gold />
              <Stat value={`${retention?.d90 ?? 0}%`} label="D90" gold />
            </View>
            <Text style={[s.subHead, { color: c.textTertiary }]}>WEEKLY COHORTS</Text>
            <View style={[s.table, { borderColor: c.border }]}>
              <View style={[s.tableRow, { backgroundColor: GOLD_DIM }]}>
                <Text style={[s.tableH, { color: GOLD, flex: 2 }]}>Cohort</Text>
                <Text style={[s.tableH, { color: GOLD }]}>Size</Text>
                <Text style={[s.tableH, { color: GOLD }]}>D1</Text>
                <Text style={[s.tableH, { color: GOLD }]}>D7</Text>
                <Text style={[s.tableH, { color: GOLD }]}>D30</Text>
              </View>
              {retention?.cohorts.map((co, i) => (
                <View key={i} style={[s.tableRow, { borderTopColor: c.border, borderTopWidth: StyleSheet.hairlineWidth }]}>
                  <Text style={[s.tableCell, { color: c.text, flex: 2 }]}>{co.label}</Text>
                  <Text style={[s.tableCell, { color: c.text }]}>{co.size}</Text>
                  <Text style={[s.tableCell, { color: c.text }]}>{co.ageDays < 1 ? '—' : `${co.d1}%`}</Text>
                  <Text style={[s.tableCell, { color: c.text }]}>{co.ageDays < 7 ? '—' : `${co.d7}%`}</Text>
                  <Text style={[s.tableCell, { color: c.text }]}>{co.ageDays < 30 ? '—' : `${co.d30}%`}</Text>
                </View>
              ))}
            </View>
            <Text style={[s.subHead, { color: c.textTertiary, marginTop: SPACE[3] }]}>CHURN BY TIER (30d inactive)</Text>
            {retention && Object.entries(retention.churnByTier).sort(([, a], [, b]) => b - a).map(([tier, cnt]) => (
              <Bar key={tier} label={tier} value={cnt} max={allUsers.length} />
            ))}
          </Section>

          {/* ═══ FUNNEL ═══ */}
          <Section id="funnel" icon={<Zap size={18} color={GOLD} />} title="Funnel">
            {funnel.map((step, i) => (
              <View key={i} style={s.funnelRow}>
                <View style={{ flex: 1 }}>
                  <Text style={[s.funnelLabel, { color: c.text }]}>{step.label}</Text>
                  <Text style={[s.funnelMeta, { color: c.textTertiary }]}>
                    {step.count} users · {step.pct}%{i > 0 ? ` · ↓${step.dropOff}% drop` : ''}
                  </Text>
                </View>
                <View style={[s.funnelBarTrack, { backgroundColor: 'rgba(128,128,128,0.12)' }]}>
                  <View style={[s.funnelBarFill, { backgroundColor: GOLD, width: `${Math.max(step.pct, 2)}%` as any }]} />
                </View>
              </View>
            ))}
          </Section>

          {/* ═══ CONTENT ═══ */}
          <Section id="content" icon={<Trophy size={18} color={GOLD} />} title="Content & Activity">
            <View style={s.statGrid}>
              <Stat value={content?.avgSetsPerWorkout ?? 0} label="Avg Sets/Workout" />
              <Stat value={formatNum(content?.totalVolume ?? 0)} label="Total Volume (lbs)" gold />
            </View>
            <Text style={[s.subHead, { color: c.textTertiary }]}>TOP 10 EXERCISES</Text>
            {content?.topExercises.map((ex, i) => (
              <RankedRow key={i} rank={i + 1} name={ex.name} value={`${ex.count}×`} />
            ))}
            <Text style={[s.subHead, { color: c.textTertiary, marginTop: SPACE[3] }]}>MOST ACTIVE GYMS</Text>
            {content?.mostActiveGyms.map((g, i) => (
              <RankedRow key={i} rank={i + 1} name={g.name} value={`${g.workouts} workouts`} />
            ))}
          </Section>

          {/* ═══ APP HEALTH ═══ */}
          <Section id="health" icon={<Shield size={18} color={GOLD} />} title="App Health">
            <View style={s.statGrid}>
              <Stat value={health?.flagsTotal ?? 0} label="Total Flags" />
              <Stat value={health?.flagsUnresolved ?? 0} label="Unresolved" gold />
              <Stat value={health?.flagsResolvedThisWeek ?? 0} label="Resolved (7d)" />
              <Stat value={health?.activeSeasons ?? 0} label="Active Seasons" />
              <Stat value={health?.seasonParticipants ?? 0} label="Participants" />
              <Stat value={`${health?.challengeSubmissionRate ?? 0}%`} label="Submission Rate" />
            </View>
          </Section>

          <View style={{ height: 40 }} />
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

// ─── Styles ──────────────────────────────────────────────────────
const s = StyleSheet.create({
  root: { flex: 1 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: SPACE[3] },
  accessDenied: { fontSize: FONT.xl, fontFamily: TYPO.heading.bold, marginTop: SPACE[3] },
  accessSub: { fontSize: FONT.sm },

  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: SPACE[5], paddingVertical: SPACE[4], borderBottomWidth: 1,
  },
  headerTitle: { fontSize: FONT.xl, fontFamily: TYPO.heading.bold, letterSpacing: 1 },

  filterRow: {
    flexDirection: 'row', alignItems: 'center', gap: SPACE[2],
    paddingHorizontal: SPACE[4], paddingVertical: SPACE[2], borderBottomWidth: 1,
  },
  filterLabel: { flex: 1, fontSize: FONT.sm, fontFamily: TYPO.body.regular },

  scroll: { padding: SPACE[4], gap: SPACE[3] },
  errorBox: { borderRadius: RADIUS.sm, padding: SPACE[3], gap: SPACE[1] },

  section: { borderWidth: 1, borderRadius: RADIUS.sm, overflow: 'hidden' },
  sectionHeader: {
    flexDirection: 'row', alignItems: 'center', gap: SPACE[2],
    paddingHorizontal: SPACE[4], paddingVertical: SPACE[3],
  },
  sectionTitle: { flex: 1, fontSize: FONT.lg, fontFamily: TYPO.heading.bold },
  sectionBody: { paddingHorizontal: SPACE[4], paddingBottom: SPACE[4] },

  statGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: SPACE[2], marginBottom: SPACE[3] },
  stat: {
    flexGrow: 1, minWidth: 80, borderWidth: 1, borderRadius: RADIUS.sm,
    padding: SPACE[3], alignItems: 'center', gap: 2,
  },
  statVal: { fontSize: FONT['2xl'], fontFamily: TYPO.heading.bold },
  statLabel: { fontSize: FONT.xs, fontFamily: TYPO.body.regular, textAlign: 'center' },
  delta: { fontSize: FONT.xs, fontWeight: WEIGHT.bold },

  subHead: {
    fontSize: FONT.xs, fontFamily: TYPO.heading.bold, letterSpacing: 1,
    marginBottom: SPACE[2], marginTop: SPACE[2],
  },

  barRow: { flexDirection: 'row', alignItems: 'center', gap: SPACE[2], marginBottom: SPACE['1.5'] },
  barLabel: { fontSize: FONT.sm, width: 110 },
  barTrack: { flex: 1, height: 8, borderRadius: 4 },
  barFill: { height: 8, borderRadius: 4 },
  barVal: { fontSize: FONT.sm, fontWeight: WEIGHT.semibold, width: 56, textAlign: 'right' },

  rankedRow: {
    flexDirection: 'row', alignItems: 'center', gap: SPACE[2],
    paddingVertical: SPACE[2], borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rank: { fontSize: FONT.sm, fontWeight: WEIGHT.bold, width: 24, textAlign: 'center' },
  rankedName: { fontSize: FONT.base, fontWeight: WEIGHT.semibold },
  rankedSub: { fontSize: FONT.xs, marginTop: 1 },
  rankedVal: { fontSize: FONT.base, fontFamily: TYPO.heading.bold },

  peakRow: {
    flexDirection: 'row', alignItems: 'center', gap: SPACE[2],
    padding: SPACE[3], borderRadius: RADIUS.sm, borderWidth: 1, marginTop: SPACE[2],
  },
  peakText: { fontSize: FONT.base, fontFamily: TYPO.heading.bold },

  table: { borderWidth: 1, borderRadius: RADIUS.sm, overflow: 'hidden' },
  tableRow: { flexDirection: 'row', paddingHorizontal: SPACE[3], paddingVertical: SPACE[2] },
  tableH: { flex: 1, fontSize: FONT.xs, fontFamily: TYPO.heading.bold, textAlign: 'center' },
  tableCell: { flex: 1, fontSize: FONT.sm, textAlign: 'center' },

  funnelRow: { flexDirection: 'row', alignItems: 'center', gap: SPACE[3], marginBottom: SPACE[3] },
  funnelLabel: { fontSize: FONT.base, fontWeight: WEIGHT.semibold },
  funnelMeta: { fontSize: FONT.xs, marginTop: 1 },
  funnelBarTrack: { width: 100, height: 10, borderRadius: 5 },
  funnelBarFill: { height: 10, borderRadius: 5 },
});
